import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getTranslationRequest,
  updateTranslationRequest,
} from "../../services/translationRequests";
import { uploadFileAndGetUrl } from "../../services/coa";
import { apiFetch } from "../../services/api";
import toast from "react-hot-toast";
import {
  TRANSLATION_TYPE_OPTIONS,
  normalizeTranslationType,
} from "../../constants/translationRequestType";

const TranslationRequestDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [translationRequest, setTranslationRequest] = useState(null);
  const [translationFile, setTranslationFile] = useState(null);
  const [methodologyFile, setMethodologyFile] = useState(null);
  const [price, setPrice] = useState(0);
  const [translationStatus, setTranslationStatus] = useState(
    "In Development – IHO",
  );
  const [paymentStatus, setPaymentStatus] = useState("Pending");
  const [adminNotes, setAdminNotes] = useState("");

  useEffect(() => {
    if (id) {
      loadTranslationRequest();
    }
  }, [id]);

  const loadTranslationRequest = async () => {
    try {
      setLoading(true);
      const response = await getTranslationRequest(id);
      if (response && response.success !== false) {
        const data = response.data || response;
        const request = data.translationRequest || data;
        setTranslationRequest(request);
        setPrice(request.price || 0);
        setTranslationStatus(
          normalizeTranslationType(request.translationStatus),
        );
        setPaymentStatus(request.paymentStatus || "Pending");
        setAdminNotes(request.adminNotes || "");
      }
    } catch (err) {
      console.error("Error loading translation request:", err);
      toast.error("Failed to load translation request");
      navigate("/requested-licences");
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e, fileType) => {
    const file = e.target.files[0];
    if (file) {
      if (fileType === "translation") {
        setTranslationFile(file);
      } else {
        setMethodologyFile(file);
      }
    }
  };

  const buildUpdateData = async () => {
    let translationFileUrl = null;
    let methodologyFileUrl = null;

    if (translationFile) {
      translationFileUrl = await uploadFileAndGetUrl(translationFile);
    }
    if (methodologyFile) {
      methodologyFileUrl = await uploadFileAndGetUrl(methodologyFile);
    }

    const updateData = {
      price: parseFloat(price) || 0,
      translationStatus,
      paymentStatus,
      adminNotes,
    };

    if (translationFileUrl) {
      updateData.translationFile = {
        url: translationFileUrl,
        fileName: translationFile.name,
        fileSize: translationFile.size,
        uploadedAt: new Date().toISOString(),
      };
    }

    if (methodologyFileUrl) {
      updateData.methodologyFile = {
        url: methodologyFileUrl,
        fileName: methodologyFile.name,
        fileSize: methodologyFile.size,
        uploadedAt: new Date().toISOString(),
      };
    }

    return updateData;
  };

  const persistChanges = async () => {
    const updateData = await buildUpdateData();
    await updateTranslationRequest(id, updateData);
    setTranslationFile(null);
    setMethodologyFile(null);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await persistChanges();
      toast.success("Translation request updated successfully");
      await loadTranslationRequest();
    } catch (err) {
      console.error("Error updating translation request:", err);
      toast.error("Failed to update translation request");
    } finally {
      setSaving(false);
    }
  };

  const handleSendInvoice = async () => {
    try {
      setSaving(true);
      await persistChanges();

      const result = await apiFetch(
        `/api/admin/translation-requests/${id}/create-invoice`,
        {
          method: "POST",
        },
      );

      if (result && result.success !== false) {
        toast.success("Invoice created successfully");
        await loadTranslationRequest();

        if (result.data?.invoice?._id) {
          navigate(`/invoices/${result.data.invoice._id}`);
        }
      } else {
        throw new Error(result?.message || "Failed to create invoice");
      }
    } catch (err) {
      console.error("Error creating invoice:", err);
      toast.error(err.message || "Failed to create invoice");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <p className="text-gray-500">Loading translation request...</p>
      </div>
    );
  }

  if (!translationRequest) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-500">Translation request not found</p>
        <button
          onClick={() => navigate("/requested-licences")}
          className="mt-4 px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600"
        >
          Back to Translation Requests
        </button>
      </div>
    );
  }

  const canSendInvoice = !translationRequest.invoiceId;

  return (
    <div className="p-6">
      <div className="mb-6">
        <button
          onClick={() => navigate("/requested-licences")}
          className="text-emerald-600 hover:text-emerald-700 mb-4 flex items-center gap-2"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M12.5 15L7.5 10L12.5 5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Back to Translation Requests
        </button>
        <h1 className="text-2xl font-bold text-gray-900">
          Translation Request Details
        </h1>
        <p className="text-gray-600 mt-1">
          Reference: {translationRequest.refNumber || "N/A"}
        </p>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Request Information
        </h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Customer
            </label>
            <p className="text-gray-900">
              {translationRequest.customerId?.name ||
                translationRequest.customerId?.email ||
                "N/A"}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              COA
            </label>
            <p className="text-gray-900">
              {translationRequest.coaId?.name || "N/A"}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Licence Reference
            </label>
            <p className="text-gray-900">
              {translationRequest.licenceId?.refNumber || "N/A"}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Submitted At
            </label>
            <p className="text-gray-900">
              {translationRequest.submittedAt
                ? new Date(translationRequest.submittedAt).toLocaleDateString(
                    "en-GB",
                    {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    },
                  )
                : "N/A"}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Requested Languages
            </label>
            <p className="text-gray-900">
              {translationRequest.selectedLanguages?.length > 0
                ? translationRequest.selectedLanguages
                    .map((l) => `${l.country} - ${l.language}`)
                    .join(", ")
                : translationRequest.customTranslationsText || "Custom Request"}
            </p>
          </div>
          {translationRequest.territory && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Territory
              </label>
              <p className="text-gray-900">{translationRequest.territory}</p>
            </div>
          )}
          {translationRequest.isUrgent && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Urgent Request
              </label>
              <span className="inline-block px-2 py-1 bg-orange-100 text-orange-800 rounded text-sm">
                Yes
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Translation Management
        </h2>

        {/* Translation Type */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Translation Type
          </label>
          <select
            value={translationStatus}
            onChange={(e) => setTranslationStatus(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
          >
            {TRANSLATION_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Payment Status */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Payment Status
          </label>
          <select
            value={paymentStatus}
            onChange={(e) => setPaymentStatus(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
          >
            <option value="Pending">Pending</option>
            <option value="Paid">Paid</option>
            <option value="Overdue">Overdue</option>
            <option value="NotApplicable">Not Applicable</option>
          </select>
        </div>

        {/* Price */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Price (£)
          </label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
            placeholder="0.00"
          />
        </div>

        {/* Translation File Upload */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Translation File
          </label>
          {translationRequest.translationFile?.url ? (
            <div className="mb-2">
              <a
                href={translationRequest.translationFile.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-emerald-600 hover:text-emerald-700 underline"
              >
                {translationRequest.translationFile.fileName ||
                  "View Translation File"}
              </a>
              <p className="text-xs text-gray-500 mt-1">
                Uploaded:{" "}
                {translationRequest.translationFile.uploadedAt
                  ? new Date(
                      translationRequest.translationFile.uploadedAt,
                    ).toLocaleDateString("en-GB")
                  : "N/A"}
              </p>
            </div>
          ) : null}
          <input
            type="file"
            onChange={(e) => handleFileChange(e, "translation")}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
            accept=".pdf,.doc,.docx"
          />
          {translationFile && (
            <p className="text-sm text-gray-600 mt-2">
              Selected: {translationFile.name}
            </p>
          )}
        </div>

        {/* Methodology File Upload */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Methodology File
          </label>
          {translationRequest.methodologyFile?.url ? (
            <div className="mb-2">
              <a
                href={translationRequest.methodologyFile.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-emerald-600 hover:text-emerald-700 underline"
              >
                {translationRequest.methodologyFile.fileName ||
                  "View Methodology File"}
              </a>
              <p className="text-xs text-gray-500 mt-1">
                Uploaded:{" "}
                {translationRequest.methodologyFile.uploadedAt
                  ? new Date(
                      translationRequest.methodologyFile.uploadedAt,
                    ).toLocaleDateString("en-GB")
                  : "N/A"}
              </p>
            </div>
          ) : null}
          <input
            type="file"
            onChange={(e) => handleFileChange(e, "methodology")}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
            accept=".pdf,.doc,.docx"
          />
          {methodologyFile && (
            <p className="text-sm text-gray-600 mt-2">
              Selected: {methodologyFile.name}
            </p>
          )}
        </div>

        {/* Admin Notes */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Admin Notes
          </label>
          <textarea
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            rows={4}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
            placeholder="Add notes about this translation request..."
          />
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>

          {canSendInvoice && (
            <button
              onClick={handleSendInvoice}
              disabled={saving}
              className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? "Processing..." : "Send Invoice"}
            </button>
          )}

          {translationRequest.invoiceId && (
            <button
              onClick={() =>
                navigate(`/invoices/${translationRequest.invoiceId}`)
              }
              className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
            >
              View Invoice
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TranslationRequestDetails;
